import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConvertedDutyCalculationValueSleepDuty } from 'components/admin-portal/Tables/CLA/DutyCalculationValueSleepDuty/types';

import { schema } from '../../utils/schema';
import { convertPayloadForSave } from '../../utils/utils';
import { mutations } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import { DutyCalculationValueSleepDutyCLACodeGroupSelect } from 'components/admin-portal/Tables/CLA/DutyCalculationValueSleepDuty/components/form/components/DutyCalculationValueSleepDutyCLACodeGroupSelect';
import ValidityFormElement from 'components/shared/CRUD/components/ValidityFormElement';
import showError from 'components/shared/showError';

type CreateEditDutyCalculationValueSleepDutyFormProps = {
  handleClose: () => void;
  rowData?: ConvertedDutyCalculationValueSleepDuty;
};

const CreateEditDutyCalculationValueSleepDutyForm = ({
  handleClose,
  rowData
}: CreateEditDutyCalculationValueSleepDutyFormProps) => {
  const { t } = useTranslation();
  const { mutateAsync: create, isLoading: isCreateLoading } =
    mutations.useCreateDutyCalculationValueSleepDuty();
  const { mutateAsync: update, isLoading: isUpdateLoading } =
    mutations.useUpdateDutyCalculationValueSleepDuty();

  const methods = useForm<ConvertedDutyCalculationValueSleepDuty>({
    defaultValues: schema.cast(rowData),
    resolver: yupResolver(schema)
  });
  const { control, handleSubmit } = methods;

  const onSubmit = (data: ConvertedDutyCalculationValueSleepDuty) => {
    const payload = convertPayloadForSave(data);
    if (data.dutyCalculationValueSleepDutyId) {
      update(payload)
        .then(handleClose)
        .catch((error) => showError(error));
    } else {
      create(payload)
        .then(handleClose)
        .catch((error) => showError(error));
    }
  };

  return (
    <>
      <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
        <MultiStepForm.Section
          title={t('dutyCalculationValue.sleepDuty.formSectionLabels.basis')}
        >
          <DutyCalculationValueSleepDutyCLACodeGroupSelect control={control} />
        </MultiStepForm.Section>
        <MultiStepForm.Section
          title={t(
            'dutyCalculationValue.sleepDuty.formSectionLabels.calculationValue'
          )}
          validateFields={[
            'hourlyWageValue',
            'hourlyWageValue20',
            'hourlyWageValue19',
            'hourlyWageValue18',
            'hourlyWageValue17',
            'hourlyWageValue16',
            'hourlyWageValue15'
          ]}
        >
          <ControlledNumericField
            name="hourlyWageValue"
            control={control}
            variant="outlined"
            label={t('dutyCalculationValue.sleepDuty.grid.hourlyWageValue')}
          />
          <ControlledNumericField
            name="hourlyWageValue20"
            control={control}
            variant="outlined"
            label={t('dutyCalculationValue.sleepDuty.grid.hourlyWageValue20')}
          />
          <ControlledNumericField
            name="hourlyWageValue19"
            control={control}
            variant="outlined"
            label={t('dutyCalculationValue.sleepDuty.grid.hourlyWageValue19')}
          />
          <ControlledNumericField
            name="hourlyWageValue18"
            control={control}
            variant="outlined"
            label={t('dutyCalculationValue.sleepDuty.grid.hourlyWageValue18')}
          />
          <ControlledNumericField
            name="hourlyWageValue17"
            control={control}
            variant="outlined"
            label={t('dutyCalculationValue.sleepDuty.grid.hourlyWageValue17')}
          />
          <ControlledNumericField
            name="hourlyWageValue16"
            control={control}
            variant="outlined"
            label={t('dutyCalculationValue.sleepDuty.grid.hourlyWageValue16')}
          />
          <ControlledNumericField
            name="hourlyWageValue15"
            control={control}
            variant="outlined"
            label={t('dutyCalculationValue.sleepDuty.grid.hourlyWageValue15')}
          />
        </MultiStepForm.Section>
        <MultiStepForm.Section
          title={t('dutyCalculationValue.sleepDuty.formSectionLabels.validity')}
          validateFields={['from', 'to']}
        >
          <ValidityFormElement />
        </MultiStepForm.Section>
        <MultiStepForm.Actions>
          <Box display="flex" ml="auto">
            <LoadingButton
              loading={isCreateLoading || isUpdateLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="primary"
            >
              {t('save')}
            </LoadingButton>
          </Box>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
};

export default CreateEditDutyCalculationValueSleepDutyForm;
